<template>
  <div v-if="intersectList[id]" ref="intersectRoot" :class="['gam-intersect', { 'is-mobile': isMobile }]">
    <!-- <transition name="intersect" mode="out-in"> -->
    <div v-if="noData && !isLoading && noDataInfo" class="gam-no-data-wrapper">
      <gam-error-info v-bind="noDataInfo" />
    </div>

    <ScrollOverflow
      v-else
      ref="scrollbar"
      :class="['gam-intersect-wrapper', { 'scroll-spacing': scrollSpacing, dropdown: dropdown }]"
      is-vertical
    >
      <div class="slot-content" :style="getIntersectStyle">
        <slot />
      </div>

      <div v-if="showMore" ref="intersectTarget" :class="['intersect-element', { 'show-more': showMore }]">
        <!-- eslint-disable max-len -->
        <svg
          class="intersect-spinner"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
          fill="white"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M8 1.78915C4.56984 1.78915 1.78915 4.56984 1.78915 8C1.78915 11.4302 4.56984 14.2108 8 14.2108C11.4302 14.2108 14.2108 11.4302 14.2108 8C14.2108 7.72386 14.4347 7.5 14.7108 7.5C14.987 7.5 15.2108 7.72386 15.2108 8C15.2108 11.9824 11.9824 15.2108 8 15.2108C4.01755 15.2108 0.789146 11.9824 0.789146 8C0.789146 4.01755 4.01755 0.789146 8 0.789146C8.27614 0.789146 8.5 1.013 8.5 1.28915C8.5 1.56529 8.27614 1.78915 8 1.78915Z"
          />
        </svg>
        <!-- eslint-enable max-len -->
      </div>
      <GamErrorInfo v-else-if="endInfo && !isLoading" v-bind="endInfo" />
    </ScrollOverflow>
    <!-- </transition> -->
  </div>
</template>

<script setup lang="ts">
import { isMobile } from '@/core/helpers/ui.helper';
import { useIntersectStore } from '@/stores/intersect.store';
import { useTableStore } from '@/stores/table.store';
import GamErrorInfo from '@/views/components/GamErrorInfo.vue';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import type { GamIntersectType } from '@/views/composables/models/components/GamIntersect';
import { useIntersectionObserver } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch, type StyleValue } from 'vue';
import ScrollOverflow from './ScrollOverflow.vue';

const scrollbar = ref<typeof ScrollOverflow>();

const props = withDefaults(defineProps<GamIntersectType>(), {
  showMore: true,
  noData: false,
  threshold: 0.5,
  rootMargin: '200px 0px 0px',
  widthSpacing: 20,
  itemsGap: 8,
  spacerPadding: isMobile.value ? 130 : 168,
  footerPadding: isMobile.value ? 20 : 42,
});

const intersectStore = useIntersectStore();
const { intersectList } = storeToRefs(intersectStore);
const tableStore = useTableStore(props.id)();
const { tableParams, isLoading } = storeToRefs(tableStore);

watch(
  () => [tableParams.value.beforeCursor, tableParams.value.afterCursor],
  ([beforeCursor, afterCursor]) => {
    if (!scrollbar.value) return;
    if (beforeCursor !== null) return;
    if (afterCursor !== null) return;
    // scrollbar.value.resetScroll();
  },
);

const intersectRoot = ref<HTMLElement>();
const intersectTarget = ref<HTMLElement>();
const emits = defineEmits([GamComponentsEmits.INFINITE]);

onMounted(() => {
  intersectStore.addIntersect(props.id, scrollbar.value, props.isLoaded);
});

const onIntersection = (intersectionObserver: IntersectionObserverEntry[]): void => {
  const { isIntersecting } = intersectionObserver[0];
  if (isIntersecting) {
    emits(GamComponentsEmits.INFINITE);
  }
};

const getIntersectStyle = computed((): StyleValue => {
  return {
    gap: `${props.itemsGap}px`,
  };
});

const { stop } = useIntersectionObserver(intersectTarget, onIntersection, {
  root: intersectRoot,
  threshold: props.threshold,
  rootMargin: props.rootMargin,
});

onUnmounted(() => {
  stop();
  intersectStore.removeIntersect(props.id);
});
</script>

<style lang="scss">
.gam-intersect {
  width: inherit;
  height: 100%;

  .missing-info-container {
    width: 100%;
  }

  .slot-content {
    scroll-behavior: smooth;
  }

  &.is-mobile {
    .gam-no-data-wrapper {
      padding: var(--spacing-mobile-top) var(--spacing-mobile-wrapper) var(--spacing-mobile-bottom);
    }

    .gam-intersect-wrapper {
      padding: 0;
    }
  }

  .gam-no-data-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    padding: var(--spacing-desktop-top) var(--spacing-xxl) var(--spacing-desktop-bottom);
    position: absolute;
    top: 0;
  }

  .intersect-loader-wrapper,
  .geo-wrapper {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 500;
    background: var(--color-dark-70);

    &.dropdown {
      -webkit-backdrop-filter: blur(15px);
      backdrop-filter: blur(15px);
      background: var(--color-dark-30);
    }
  }

  .gam-intersect-wrapper {
    width: calc(100%);
    height: 100%;
    max-height: 100svh;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex-shrink: 0;
    align-self: stretch;

    .slot-content,
    .skeleton-loader {
      display: flex;
      width: 100%;
      flex-direction: column;
    }

    .skeleton-loader {
      gap: var(--spacing-md);

      .skeleton-item {
        border-radius: var(--radius-small);
        cursor: pointer;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: var(--spacing-md);
        flex-wrap: nowrap;
        overflow: hidden;
        min-height: 5rem;
        width: 100%;
        padding: var(--spacing-md) var(--spacing-l);
      }
    }

    &.ps {
      &.dropdown {
        .intersect-loader-wrapper {
          &.show-more {
            padding-bottom: var(--spacing-l);
          }
        }
      }
    }

    .intersect-element {
      width: 100%;
      display: flex;
      justify-content: center;

      &.show-more {
        padding-bottom: var(--spacing-xxl);
      }
    }

    .intersect-spinner {
      animation: intersect-spin 1s var(--cubic-bezier) infinite;
    }
  }
}

@keyframes intersect-spin {
  from {
    rotate: 0deg;
  }
  to {
    rotate: 360deg;
  }
}
</style>
